// PLAN-cockpit-v2 Phase 2-6: Detail data fetching (R2 record + D1 metadata)
//
// /api/entries/:id (Entry の R2 JSON full)
// /api/articles/:id (Article の R2 JSON full、最新版 resolve)
// /api/domains/:item_type/:id (declared / curated / inferred)

import { useEffect, useState } from 'react'
import { apiFetch } from '../../api/client'

interface DomainBadgeRow {
  domain: string
  provenance: 'declared' | 'curated' | 'inferred'
  polarity?: 'include' | 'exclude'
  confidence?: number | null
  source?: string | null
  reason?: string | null
}

export interface DetailItem {
  id: string
  itemType: 'entry' | 'article'
  title: string
  content: string
  one_line: string | null
  key_points: string[]    // PLAN-domain-classifier-iter2 (2026-05-11): abstract.key_points を要点 box で表示
  origin: string
  subjectivity: string
  visibility: string
  lifecycle_stage?: string | null
  source: { tool?: string | null; medium?: string | null; project?: string | null } | null
  source_actor: string | null
  source_context: string | null
  declared_domains: string[]
  curated: DomainBadgeRow[]
  inferred: DomainBadgeRow[]
  previous_version: string | null
  source_article_ids: string[]
  source_entry_ids: string[]
  stale_by: string | null
  review_by: string | null
  created_at: string
  // PLAN-domain-classifier-iter2 (2026-05-11): Article 評価 / メタ表示用 (Entry のときは undefined)
  article_meta?: {
    theme?: string
    version_label?: string | null
    confidence?: string
    applicability?: string
    confidence_basis?: string | null
  }
  // PLAN-pvw-dsl-adoption (2026-05-13): PVW Core projection + R2 decision payload
  decision?: import('../../types').PvwDecisionPayload | null
  migration_status?: import('../../types').MigrationStatus | null
  text_schema_status?: import('../../types').TextSchemaStatus | null
  has_decision?: boolean | number | null
  x_status?: import('../../types').DecisionXStatus | null
  decision_size_class?: import('../../types').DecisionSizeClass | null
}

// API field 名は `declared_domains` / `curated_domains` / `inferred_domains` (worker-entry/src/routes/domains.ts)
interface DomainsApiResp {
  declared_domains?: string[]
  curated_domains?: (DomainBadgeRow & { polarity?: string; updated_by?: string | null; updated_at?: string })[]
  inferred_domains?: DomainBadgeRow[]
}

interface EntryApiResp {
  id: string
  title: string
  content?: string
  abstract?: { one_line?: string; key_points?: string[] }
  origin: string
  subjectivity: string
  visibility?: string
  source?: { tool?: string; medium?: string; project?: string }
  source_actor?: string | null
  source_context?: string | null
  source_project?: string | null
  declared_domains?: string[]
  source_article_ids?: string[]
  created_at: string
  // PVW projection (PLAN-pvw-dsl-adoption)
  migration_status?: string
  pvw_compat_status?: string
}

interface ArticleApiResp {
  resolved_id: string
  article: {
    id: string
    title: string
    content?: string
    abstract?: { one_line?: string; key_points?: string[] }
    origin: string
    subjectivity: string
    visibility?: string
    source?: { tool?: string; medium?: string; project?: string }
    source_project?: string | null
    previous_version?: string | null
    declared_domains?: string[]
    source_article_ids?: string[]
    article_meta?: {
      source_entry_ids?: string[]
      lifecycle_stage?: string
      stale_by?: string | null
      review_by?: string | null
      theme?: string
      version_label?: string | null
      confidence?: string
      applicability?: string
      confidence_basis?: string | null
    }
    created_at: string
    // PVW Core: R2 Article body の decision payload
    decision?: import('../../types').PvwDecisionPayload
  }
  // PVW projection (Article read response envelope)
  migration_status?: string
  text_schema_status?: string
  has_decision?: number | boolean
  decision_hash?: string
  x_status?: string
  decision_size_class?: string
}

export function useDetailFetch(itemId: string | null, itemType: 'entry' | 'article' | null) {
  const [item, setItem] = useState<DetailItem | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!itemId || !itemType) {
      setItem(null)
      return
    }
    const ac = new AbortController()
    setLoading(true)
    setError(null)

    async function fetchAll() {
      try {
        const path = itemType === 'article' ? `/api/articles/${itemId}` : `/api/entries/${itemId}`
        const [body, domains] = await Promise.all([
          apiFetch<EntryApiResp | ArticleApiResp>(path, { signal: ac.signal }),
          apiFetch<DomainsApiResp>(`/api/domains/${itemType}/${itemId}`, { signal: ac.signal }).catch(() => ({} as DomainsApiResp)),
        ])

        let parsed: DetailItem
        if (itemType === 'article') {
          const a = (body as ArticleApiResp).article
          parsed = {
            id: a.id,
            itemType: 'article',
            title: a.title,
            content: a.content ?? '',
            one_line: a.abstract?.one_line ?? null,
            key_points: a.abstract?.key_points ?? [],
            origin: a.origin,
            subjectivity: a.subjectivity,
            visibility: a.visibility ?? 'private',
            lifecycle_stage: a.article_meta?.lifecycle_stage ?? null,
            source: { tool: a.source?.tool ?? null, medium: a.source?.medium ?? null, project: a.source?.project ?? a.source_project ?? null },
            source_actor: null,
            source_context: null,
            declared_domains: a.declared_domains ?? [],
            curated: (domains.curated_domains ?? []) as DomainBadgeRow[],
            inferred: domains.inferred_domains ?? [],
            previous_version: a.previous_version ?? null,
            source_article_ids: a.source_article_ids ?? [],
            source_entry_ids: a.article_meta?.source_entry_ids ?? [],
            stale_by: a.article_meta?.stale_by ?? null,
            review_by: a.article_meta?.review_by ?? null,
            created_at: a.created_at,
            article_meta: {
              theme: a.article_meta?.theme,
              version_label: a.article_meta?.version_label,
              confidence: a.article_meta?.confidence,
              applicability: a.article_meta?.applicability,
              confidence_basis: a.article_meta?.confidence_basis,
            },
            // PLAN-pvw-dsl-adoption (2026-05-13): PVW Core projection + decision
            decision: (a.decision as import('../../types').PvwDecisionPayload | undefined) ?? null,
            migration_status: ((body as ArticleApiResp).migration_status as import('../../types').MigrationStatus | undefined) ?? null,
            text_schema_status: ((body as ArticleApiResp).text_schema_status as import('../../types').TextSchemaStatus | undefined) ?? null,
            has_decision: ((body as ArticleApiResp).has_decision as number | boolean | undefined) ?? (a.decision ? 1 : 0),
            x_status: ((body as ArticleApiResp).x_status as import('../../types').DecisionXStatus | undefined) ?? null,
            decision_size_class: ((body as ArticleApiResp).decision_size_class as import('../../types').DecisionSizeClass | undefined) ?? null,
          }
        } else {
          const e = body as EntryApiResp
          parsed = {
            id: e.id,
            itemType: 'entry',
            title: e.title,
            content: e.content ?? '',
            one_line: e.abstract?.one_line ?? null,
            key_points: e.abstract?.key_points ?? [],
            origin: e.origin,
            subjectivity: e.subjectivity,
            visibility: e.visibility ?? 'private',
            lifecycle_stage: null,
            source: { tool: e.source?.tool ?? null, medium: e.source?.medium ?? null, project: e.source?.project ?? e.source_project ?? null },
            source_actor: e.source_actor ?? null,
            source_context: e.source_context ?? null,
            declared_domains: e.declared_domains ?? [],
            curated: (domains.curated_domains ?? []) as DomainBadgeRow[],
            inferred: domains.inferred_domains ?? [],
            previous_version: null,
            source_article_ids: e.source_article_ids ?? [],
            source_entry_ids: [],
            stale_by: null,
            review_by: null,
            created_at: e.created_at,
            // PLAN-pvw-dsl-adoption (2026-05-13): Entry にも migration_status だけ propagation
            migration_status: (e.migration_status as import('../../types').MigrationStatus | undefined) ?? null,
          }
        }

        setItem(parsed)
        setLoading(false)
      } catch (e: unknown) {
        if ((e as { name?: string }).name === 'AbortError') return
        setError(e instanceof Error ? e.message : 'Failed to load detail')
        setLoading(false)
      }
    }

    fetchAll()
    return () => ac.abort()
  }, [itemId, itemType])

  return { item, loading, error }
}
